import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Phone, MapPin, Mail } from "lucide-react";
import { useI18n } from "../context/I18nContext";

const Topbar = () => {
  const { t } = useI18n();
  return (
    <div className="fixed top-4 left-4 right-4 z-50 flex justify-between items-center pointer-events-none">
      <Link
        to="/#portfolio"
        data-testid="demo-back"
        className="pointer-events-auto inline-flex items-center gap-2 bg-zinc-950/80 backdrop-blur border border-white/10 px-3 py-2 text-xs text-zinc-200 hover:bg-zinc-900"
      >
        <ArrowLeft size={12} /> {t("demos.back")}
      </Link>
      <Link
        to="/simulateur"
        data-testid="demo-cta-quote"
        className="pointer-events-auto inline-flex items-center gap-2 bg-white text-zinc-950 px-3 py-2 text-xs hover:bg-zinc-200"
      >
        {t("demos.cta_quote")} <ArrowUpRight size={12} />
      </Link>
    </div>
  );
};

export default function ExempleVitrine() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="demo-vitrine">
      <Topbar />

      {/* Site nav */}
      <header className="px-8 py-6 flex items-center justify-between border-b border-stone-200">
        <p className="font-display text-2xl tracking-tighter italic">Maison Lautrec</p>
        <nav className="hidden md:flex gap-8 text-xs uppercase tracking-[0.2em] text-stone-600">
          <a href="#projets" className="hover:text-stone-900">Projets</a>
          <a href="#approche" className="hover:text-stone-900">Approche</a>
          <a href="#equipe" className="hover:text-stone-900">Équipe</a>
          <a href="#contact" className="hover:text-stone-900">Contact</a>
        </nav>
        <span className="text-xs uppercase tracking-[0.2em] text-stone-500">Bordeaux · 2024</span>
      </header>

      {/* Hero */}
      <section className="px-8 py-20 md:py-32 max-w-6xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-6">— Architecture intérieure</p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[1.02] max-w-4xl">
          L'art de l'<em className="italic text-stone-600">espace</em>, du sol au plafond.
        </h1>
        <p className="mt-10 max-w-xl text-stone-600 leading-relaxed">
          Atelier d'architecture intérieure indépendant fondé en 2019. Nous concevons des
          intérieurs sur mesure pour des particuliers exigeants, en France et en Europe.
        </p>
        <div className="mt-12 flex gap-3">
          <a href="#projets" className="bg-stone-900 text-stone-50 px-6 py-3 text-sm hover:bg-stone-800">
            Voir nos projets
          </a>
          <a href="#contact" className="border border-stone-900 px-6 py-3 text-sm hover:bg-stone-900 hover:text-stone-50">
            Prendre rendez-vous
          </a>
        </div>
      </section>

      {/* Projects grid */}
      <section id="projets" className="px-8 py-20 max-w-6xl mx-auto">
        <div className="flex items-end justify-between mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-stone-500">— Sélection 2023-2025</p>
          <p className="font-display text-2xl italic text-stone-600">9 projets</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {[
            { name: "Appartement Haussmannien", loc: "Paris 7e", span: "md:col-span-7", color: "bg-amber-100" },
            { name: "Villa Méditerranée", loc: "Biarritz", span: "md:col-span-5", color: "bg-rose-100" },
            { name: "Loft Industriel", loc: "Bordeaux", span: "md:col-span-5", color: "bg-stone-200" },
            { name: "Maison de campagne", loc: "Lubéron", span: "md:col-span-7", color: "bg-emerald-100" },
          ].map((p) => (
            <article key={p.name} className={`relative aspect-[4/3] overflow-hidden ${p.span} ${p.color} group`}>
              <div className="absolute inset-0 flex items-end p-6">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-700 mb-1">{p.loc}</p>
                  <p className="font-display text-2xl">{p.name}</p>
                </div>
              </div>
              <div className="absolute top-4 right-4 w-9 h-9 border border-stone-700 flex items-center justify-center group-hover:bg-stone-900 group-hover:text-stone-50 transition-colors">
                <ArrowUpRight size={14} />
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Approach */}
      <section id="approche" className="px-8 py-20 bg-stone-100">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-6">— Notre approche</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tighter">
              Trois <em className="italic">piliers</em>.
            </h2>
          </div>
          <div className="md:col-span-7 space-y-10">
            {[
              { n: "01", t: "Écouter", d: "Chaque projet débute par une compréhension fine de l'usage, du rythme de vie et des contraintes du lieu." },
              { n: "02", t: "Dessiner", d: "Plans, perspectives 3D et choix matières — tout est validé avant la moindre démolition." },
              { n: "03", t: "Livrer", d: "Maîtrise d'œuvre complète, suivi de chantier et finitions soignées. Vous emménagez dans un projet abouti." },
            ].map((p) => (
              <div key={p.n} className="border-t border-stone-300 pt-6 grid grid-cols-12 gap-4">
                <p className="col-span-2 font-mono text-xs text-stone-500">{p.n}</p>
                <div className="col-span-10">
                  <p className="font-display text-2xl mb-2">{p.t}</p>
                  <p className="text-sm text-stone-600 leading-relaxed">{p.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="px-8 py-20 max-w-6xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-6">— Contact</p>
        <h2 className="font-display text-4xl md:text-5xl tracking-tighter mb-10">
          Parlons de votre <em className="italic">projet</em>.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl">
          <div className="border-t border-stone-300 pt-4">
            <Mail size={14} className="mb-3 text-stone-500" />
            <p className="text-sm">contact@maison-lautrec.fr</p>
          </div>
          <div className="border-t border-stone-300 pt-4">
            <Phone size={14} className="mb-3 text-stone-500" />
            <p className="text-sm">+33 5 56 12 34 56</p>
          </div>
          <div className="border-t border-stone-300 pt-4">
            <MapPin size={14} className="mb-3 text-stone-500" />
            <p className="text-sm">15 cours du Médoc, Bordeaux</p>
          </div>
        </div>
      </section>

      <footer className="px-8 py-10 border-t border-stone-200 text-xs text-stone-500 flex justify-between">
        <p>© 2025 Maison Lautrec — Tous droits réservés.</p>
        <p className="font-display italic">Démo Versant Studio</p>
      </footer>
    </div>
  );
}
